import React, { useCallback, useEffect, useState } from "react";
import { format, subMonths } from "date-fns";
import { saveAs } from "file-saver";
import { Mail, CheckCircle2, AlertTriangle, FileArchive, FileText, Send, CalendarSearch, Loader2, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { carregarFechamento, enviosDoModelo } from "./resumo";
import { gerarPdfFechamentoFiscal } from "../../../util/reports/report-fechamento-fiscal";
import { mensagemDeErro } from "../hooks";
import { handleMessageError } from "../../../components/toast";
import MessageDialog from "../../../components/ui/dialog/MessageDialog";
import AlertMessage from "../../../components/alert-message";
import reqFechamentoFiscal from "../../../services/reqEMail/reqFechamentoFiscal";
import LibDateTime from "../../../util/libDateTime";

const MESES = [
	{ valor: "01", rotulo: "Janeiro" },
	{ valor: "02", rotulo: "Fevereiro" },
	{ valor: "03", rotulo: "Março" },
	{ valor: "04", rotulo: "Abril" },
	{ valor: "05", rotulo: "Maio" },
	{ valor: "06", rotulo: "Junho" },
	{ valor: "07", rotulo: "Julho" },
	{ valor: "08", rotulo: "Agosto" },
	{ valor: "09", rotulo: "Setembro" },
	{ valor: "10", rotulo: "Outubro" },
	{ valor: "11", rotulo: "Novembro" },
	{ valor: "12", rotulo: "Dezembro" },
];

const MODELOS = [
	{ chave: "nfe", numero: 55, nome: "NF-e (Mod. 55)" },
	{ chave: "nfce", numero: 65, nome: "NFC-e (Mod. 65)" },
];

const ANO_ATUAL = new Date().getFullYear();
const ANOS = [0, 1, 2].map((i) => String(ANO_ATUAL - i));
const MES_ANTERIOR = subMonths(new Date(), 1);

export default function FechamentoFiscal() {
	const [mesSelecionado, setMesSelecionado] = useState(format(MES_ANTERIOR, "MM"));
	const [anoSelecionado, setAnoSelecionado] = useState(format(MES_ANTERIOR, "yyyy"));
	const [fase, setFase] = useState("INICIAL"); // INICIAL | ANALISANDO | ANALISADO
	const [data, setData] = useState(null);

	// Estados dos E-mails
	const [emailContador, setEmailContador] = useState("");
	const [emailCopia, setEmailCopia] = useState("");
	const [errosExibidos, setErrosExibidos] = useState(false);

	const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	const emailContadorValido = regexEmail.test(emailContador);
	const emailCopiaValido = emailCopia.trim() === "" || regexEmail.test(emailCopia);

	useEffect(() => {
		reqFechamentoFiscal
			.ultimoEnvio()
			.then(({ data: envio }) => {
				setEmailContador((atual) => atual || envio.email_para || "");
				setEmailCopia((atual) => atual || envio.email_copia || "");
			})
			.catch(() => {});
	}, []);

	const handleAnalisar = async () => {
		setFase("ANALISANDO");
		try {
			setData(await carregarFechamento(anoSelecionado, mesSelecionado));
			setFase("ANALISADO");
		} catch (erro) {
			handleMessageError(mensagemDeErro(erro));
			setFase("INICIAL");
		}
	};

	const nomeMes = MESES.find((m) => m.valor === mesSelecionado)?.rotulo;

	return (
		<div className="mt-14 flex flex-col h-[calc(100vh-6.5rem)] bg-gray-100 overflow-y-auto">
			{/* Cabeçalho Fixo (Padrão do Sistema) */}
			<div className="shrink-0 bg-white border-b px-6 py-4 flex items-start justify-between gap-4">
				<div className="flex items-center gap-3">
					<FileArchive size={22} className="text-customBlue-primary" />
					<div>
						<h1 className="text-lg font-semibold text-gray-800">Fechamento Fiscal</h1>
						<p className="text-sm text-gray-500">Conferência e transmissão para contabilidade</p>
					</div>
				</div>
			</div>

			{/* Área Principal - Fluxo de Trabalho */}
			<main className="p-6 max-w-5xl mx-auto w-full flex flex-col gap-6">

				<AlertMessage title="Para que serve" className="mt-0">
					Confere as notas fiscais do mês e envia os XMLs e o relatório para a contabilidade.
				</AlertMessage>

				{/* 1. Área de Configuração (Competência + Email) - Sempre Visível */}
				<div className="bg-white rounded-md border border-gray-300 shadow-sm overflow-hidden">
					<div className="p-5 flex flex-col gap-4">
						
						{/* Linha 1: Competência e Botão Analisar */}
						<div className="flex items-end gap-3">
							<div className="flex flex-col gap-1 w-48">
								<label className="text-xs font-semibold text-gray-600 uppercase">Mês Competência</label>
								<select
									disabled={fase === "ANALISANDO"}
									className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-customBlue-primary bg-white disabled:bg-gray-50"
									value={mesSelecionado}
									onChange={(e) => {
										setMesSelecionado(e.target.value);
										setFase("INICIAL");
									}}
								>
									{MESES.map((m) => (
										<option key={m.valor} value={m.valor}>{m.rotulo}</option>
									))}
								</select>
							</div>
							<div className="flex flex-col gap-1 w-28">
								<label className="text-xs font-semibold text-gray-600 uppercase">Ano</label>
								<select
									disabled={fase === "ANALISANDO"}
									className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-customBlue-primary bg-white disabled:bg-gray-50"
									value={anoSelecionado}
									onChange={(e) => {
										setAnoSelecionado(e.target.value);
										setFase("INICIAL");
									}}
								>
									{ANOS.map((a) => (
										<option key={a} value={a}>{a}</option>
									))}
								</select>
							</div>
							<button 
								onClick={handleAnalisar}
								disabled={fase === "ANALISANDO"}
								className="btn btn-primary h-[38px] px-6 ml-1 flex items-center justify-center min-w-[120px]"
							>
								{fase === "ANALISANDO" ? <Loader2 size={16} className="animate-spin" /> : "Analisar"}
							</button>
						</div>

						{/* Linha 2: Configurações de E-mail */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
							<div>
								<div className="flex items-center justify-between mb-1">
									<label className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-1.5">
										<Mail size={12} /> Para (Contador)
									</label>
								</div>
								<input
									type="email"
									value={emailContador}
									onChange={(e) => { setEmailContador(e.target.value); setErrosExibidos(false); }}
									className={`w-full border rounded px-3 py-2 text-sm focus:outline-none ${
										errosExibidos && !emailContadorValido
											? "border-red-400 focus:border-red-500 bg-red-50 text-red-900"
											: "border-gray-300 focus:border-customBlue-primary"
									}`}
									placeholder="contador@escritorio.com"
								/>
								{errosExibidos && !emailContadorValido && (
									<p className="text-xs text-red-500 font-medium mt-1.5">E-mail obrigatório ou em formato inválido.</p>
								)}
							</div>
							<div>
								<div className="flex items-center justify-between mb-1">
									<label className="text-xs font-semibold text-gray-600 uppercase flex items-center gap-1.5">
										<Mail size={12} /> Cópia (Empresa)
									</label>
									{emailCopia.trim() === "" && (
										<span className="text-[10px] font-semibold text-gray-400 uppercase">Opcional</span>
									)}
								</div>
								<input
									type="email"
									value={emailCopia}
									onChange={(e) => { setEmailCopia(e.target.value); setErrosExibidos(false); }}
									className={`w-full border rounded px-3 py-2 text-sm focus:outline-none ${
										errosExibidos && !emailCopiaValido
											? "border-red-400 focus:border-red-500 bg-red-50 text-red-900"
											: "border-gray-300 focus:border-customBlue-primary"
									}`}
									placeholder="Opcional (ex: admin@empresa.com)"
								/>
								{errosExibidos && !emailCopiaValido && (
									<p className="text-xs text-red-500 font-medium mt-1.5">O e-mail preenchido possui formato inválido.</p>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* 2. Empty State (Se não analisou) */}
				{fase === "INICIAL" && (
					<div className="flex flex-col items-center justify-center py-16 text-gray-400">
						<CalendarSearch size={48} className="mb-4 opacity-50" />
						<p className="text-md font-medium text-gray-500">Selecione a competência e revise os e-mails acima.</p>
						<p className="text-sm text-gray-400 mt-1">Após clicar em Analisar, as opções de transmissão serão liberadas.</p>
					</div>
				)}

				{/* 3. Loading State (Skeletons) */}
				{fase === "ANALISANDO" && (
					<div className="flex flex-col gap-4 animate-pulse">
						<div className="h-4 bg-gray-300 rounded w-48 mb-2"></div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div className="h-48 bg-gray-200 rounded-md border border-gray-300"></div>
							<div className="h-48 bg-gray-200 rounded-md border border-gray-300"></div>
						</div>
					</div>
				)}

				{/* 4. Resultados da Análise */}
				{fase === "ANALISADO" && (
					<div className="flex flex-col gap-4 animate-in fade-in duration-300">
						<h3 className="text-sm font-semibold text-gray-500 mb-1">
							Resultado da conferência: <span className="text-gray-800">{nomeMes}/{anoSelecionado}</span>
						</h3>
						
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{MODELOS.map(({ chave, numero, nome }) => (
								<CardCompacto
									key={chave}
									modelo={nome}
									numeroModelo={numero}
									ano={anoSelecionado}
									mes={mesSelecionado}
									competencia={`${nomeMes}/${anoSelecionado}`}
									arquivo={`fechamento-${chave}-${anoSelecionado}-${mesSelecionado}.pdf`}
									empresa={data.empresa}
									dados={data[chave]}
									enviosIniciais={data.envios[chave]}
									falhaInicial={data.falhaEnvios}
									emailContador={emailContador}
									emailCopia={emailCopia}
									onFalhaValidacao={() => setErrosExibidos(true)}
								/>
							))}
						</div>
					</div>
				)}
			</main>
		</div>
	);
}

// Etapas do processo de transmissão
const ETAPAS_ENVIO = [
	{ etapa: "GERANDO_PDF", rotulo: "Gerando relatório em PDF..." },
	{ etapa: "GERANDO_ZIP", rotulo: "Separando e compactando os XMLs..." },
	{ etapa: "ENVIANDO_EMAIL", rotulo: "Enviando e-mail para contabilidade..." },
];

const EM_ANDAMENTO = ["PENDENTE", "PROCESSANDO"];
const INTERVALO_CONSULTA = 3000;

const CONTADORES = [
	{ campo: "emitidas", rotulo: "Emitidas", titulo: "Autorizadas" },
	{ campo: "canceladas", rotulo: "Canceladas", titulo: "Canceladas" },
	{ campo: "inutilizadas", rotulo: "Inutilizadas", titulo: "Inutilizadas" },
	{ campo: "denegadas", rotulo: "Denegadas", titulo: "Denegadas", soComValor: true },
];

const dataHora = (iso) => LibDateTime.parseDelphiLocal(iso, "dd/MM/yyyy HH:mm");

// Card Compacto com Botão de Transmissão Individual
function CardCompacto({ modelo, numeroModelo, ano, mes, competencia, arquivo, empresa, dados, enviosIniciais, falhaInicial, emailContador, emailCopia, onFalhaValidacao }) {
	const [expandido, setExpandido] = useState(false);

	// Controle de Transmissão
	const [envios, setEnvios] = useState(enviosIniciais);
	const [falhaConsulta, setFalhaConsulta] = useState(falhaInicial);
	const [solicitando, setSolicitando] = useState(false);
	const [confirmarReenvio, setConfirmarReenvio] = useState(false);

	// Controle do Modal de Confirmação
	const [modalAberto, setModalAberto] = useState(false);

	// Controle do Modal de Erro de E-mail
	const [erroEmail, setErroEmail] = useState(null);

	const qtdGaps = dados.lacunas.reduce((total, lacuna) => total + lacuna.ate - lacuna.de + 1, 0);
	const qtdPendencias = (dados.rejeitadas || 0) + (dados.pendentes || 0) + qtdGaps;
	const temErro = qtdPendencias > 0;
	const semMovimento = CONTADORES.every(({ campo }) => dados[campo] === 0);
	const contadores = CONTADORES.filter(({ campo, soComValor }) => !soComValor || dados[campo] > 0);

	const atual = envios[0];
	const ativo = EM_ANDAMENTO.includes(atual?.status);
	const concluido = atual?.status === "CONCLUIDO";
	const enviados = envios.filter((envio) => envio.status === "CONCLUIDO");
	const etapaAtual = ETAPAS_ENVIO.findIndex(({ etapa }) => atual?.status === "PROCESSANDO" && etapa === atual.etapa);

	useEffect(() => {
		setEnvios(enviosIniciais);
		setFalhaConsulta(falhaInicial);
	}, [enviosIniciais, falhaInicial]);

	const atualizarEnvios = useCallback(async () => {
		try {
			const resposta = await reqFechamentoFiscal.listar({ ano, mes });
			setEnvios(enviosDoModelo(resposta.data, numeroModelo));
			setFalhaConsulta(null);
		} catch (erro) {
			setFalhaConsulta(mensagemDeErro(erro));
		}
	}, [ano, mes, numeroModelo]);

	useEffect(() => {
		if (!ativo || falhaConsulta) return undefined;

		const temporizador = setTimeout(atualizarEnvios, INTERVALO_CONSULTA);
		return () => clearTimeout(temporizador);
	}, [envios, ativo, falhaConsulta, atualizarEnvios]);

	const iniciarTransmissao = async () => {
		setModalAberto(false);
		setConfirmarReenvio(false);
		setSolicitando(true);

		try {
			await reqFechamentoFiscal.transmitir({
				ano: Number(ano),
				mes: Number(mes),
				modelo: numeroModelo,
				email_para: emailContador.trim(),
				email_copia: emailCopia.trim(),
			});
		} catch (erro) {
			handleMessageError(mensagemDeErro(erro));
		}

		await atualizarEnvios();
		setSolicitando(false);
	};

	const confirmarPendencias = () => {
		setModalAberto(false);

		if (enviados.length > 0) {
			setConfirmarReenvio(true);
			return;
		}
		iniciarTransmissao();
	};

	const visualizarRelatorio = () => {
		const pdf = gerarPdfFechamentoFiscal({ modelo, competencia, empresa, dados }).output("blob");
		const aba = window.open(URL.createObjectURL(pdf), "_blank");

		if (!aba) saveAs(pdf, arquivo);
	};

	const handleTransmitir = () => {
		const regexEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		const emailContadorValido = regexEmail.test(emailContador);
		const emailCopiaValido = emailCopia.trim() === "" || regexEmail.test(emailCopia);

		if (!emailContadorValido) {
			onFalhaValidacao();
			setErroEmail("O e-mail do contador está inválido ou vazio. Por favor, corrija na área de configurações antes de transmitir.");
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}

		if (!emailCopiaValido) {
			onFalhaValidacao();
			setErroEmail("O e-mail de cópia está com um formato inválido. Corrija ou deixe em branco antes de transmitir.");
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}

		if (temErro) {
			setModalAberto(true);
			return;
		}
		confirmarPendencias();
	};

	return (
		<>
			<div className={`bg-white border ${temErro && !concluido ? "border-amber-400" : concluido ? "border-emerald-500" : "border-gray-300"} rounded-md flex flex-col h-full relative`}>
				<div className="p-4 flex flex-col gap-3 flex-1">
					{/* Header do Card */}
					<div className="flex items-center justify-between">
						<h4 className="text-sm font-bold text-gray-800">Transmissão {modelo}</h4>
						{ativo ? (
							<span className="text-xs font-semibold text-sky-600 bg-sky-50 px-2 py-0.5 rounded border border-sky-200">
								Transmitindo
							</span>
						) : concluido ? (
							<span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
								Enviado
							</span>
						) : atual?.status === "ERRO" ? (
							<span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-200">
								Falha no envio
							</span>
						) : atual?.status === "VERIFICAR" ? (
							<span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
								Verificar envio
							</span>
						) : temErro ? (
							<span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
								Revisão necessária
							</span>
						) : (
							<span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
								Tudo Certo
							</span>
						)}
					</div>

					{/* Resumo em linha única */}
					<div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm">
						{contadores.map(({ campo, rotulo, titulo }, indice) => (
							<React.Fragment key={campo}>
								{indice > 0 && <div className="w-px h-4 bg-gray-300"></div>}
								<div className="flex items-center gap-1.5" title={titulo}>
									<span className="font-semibold text-gray-700">{dados[campo]}</span>
									<span className="text-xs text-gray-500">{rotulo}</span>
								</div>
							</React.Fragment>
						))}
					</div>

					{/* Alerta de Pendências Compacto */}
					{temErro && !concluido && (
						<div className="flex items-center justify-between mt-1">
							<div className="flex items-center gap-1.5 text-amber-600 text-sm font-medium">
								<AlertTriangle size={14} />
								{qtdPendencias} pendência(s) encontrada(s)
							</div>
							<button 
								onClick={() => setExpandido(!expandido)}
								disabled={ativo}
								className="text-xs font-semibold text-customBlue-primary hover:underline flex items-center gap-1 disabled:opacity-50 disabled:no-underline"
							>
								{expandido ? "Ocultar detalhes" : "Ver detalhes"}
								{expandido ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
							</button>
						</div>
					)}

					{/* Botão de Transmissão ou Checklist de Envio */}
					<div className="mt-auto pt-3 border-t border-gray-100">
						{falhaConsulta ? (
							<div className="flex flex-col gap-2 p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700">
								<span>Não foi possível consultar o andamento da transmissão. {falhaConsulta}</span>
								<button
									onClick={atualizarEnvios}
									className="self-start flex items-center gap-1.5 font-semibold text-red-700 hover:underline"
								>
									<RefreshCw size={12} /> Consultar novamente
								</button>
							</div>
						) : ativo ? (
							<div className="flex flex-col gap-2 p-2 bg-gray-50 rounded border border-gray-200">
								{etapaAtual === -1 ? (
									<div className="flex items-center gap-2 text-xs font-semibold text-gray-800">
										<Loader2 size={14} className="animate-spin text-customBlue-primary" />
										Aguardando o início do processamento...
									</div>
								) : (
									ETAPAS_ENVIO.slice(0, etapaAtual + 1).map(({ etapa, rotulo }, index) => (
										<div key={etapa} className="flex items-center gap-2 text-xs font-semibold">
											{index === etapaAtual ? (
												<Loader2 size={14} className="animate-spin text-customBlue-primary" />
											) : (
												<CheckCircle2 size={14} className="text-emerald-500" />
											)}
											<span className={index === etapaAtual ? "text-gray-800" : "text-gray-500"}>{rotulo}</span>
										</div>
									))
								)}
							</div>
						) : (
							<div className="flex flex-col gap-2">
								{atual?.status === "ERRO" && (
									<div className="p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700">
										<span className="font-semibold">Falha na transmissão de {dataHora(atual.data_cadastro)}:</span> {atual.erro}
									</div>
								)}
								{atual?.status === "VERIFICAR" && (
									<div className="p-2 bg-amber-50 rounded border border-amber-200 text-xs text-amber-800">
										<span className="font-semibold">Transmissão de {dataHora(atual.data_cadastro)} interrompida no envio:</span> {atual.erro}
									</div>
								)}
								{enviados.length > 0 && (
									<div className="flex items-center gap-1.5 text-xs text-emerald-700">
										<CheckCircle2 size={14} />
										{enviados.length > 1 ? `Enviado ${enviados.length} vezes, a última` : "Enviado"} em {dataHora(enviados[0].data_fim)} para {enviados[0].email_para}
									</div>
								)}
								<div className="flex gap-2">
									<button
										onClick={visualizarRelatorio}
										className="h-[36px] px-3 flex items-center justify-center gap-2 rounded text-sm font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
									>
										<FileText size={14} /> Visualizar relatório
									</button>
									<button
										onClick={handleTransmitir}
										disabled={semMovimento || solicitando}
										className={`flex-1 h-[36px] flex items-center justify-center gap-2 rounded text-sm font-semibold transition-colors ${
											temErro
											? "bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300"
											: "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-300"
										} disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200`}
									>
										{solicitando ? (
											<Loader2 size={14} className="animate-spin" />
										) : (
											<>
												<Send size={14} /> {enviados.length > 0 ? "Reenviar" : "Transmitir Arquivos"}
											</>
										)}
									</button>
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Detalhes Expandidos */}
				{temErro && expandido && !ativo && !concluido && (
					<div className="bg-amber-50/50 border-t border-amber-100 p-4 flex flex-col gap-3 text-sm animate-in slide-in-from-top-2 duration-200">
						
						{dados.rejeitadas > 0 && (
							<div>
								<p className="font-semibold text-gray-800 flex items-center gap-1.5 mb-1">
									<span className="w-2 h-2 rounded-full bg-red-500"></span>
									{dados.rejeitadas} Nota(s) Rejeitada(s)
								</p>
								<p className="text-xs text-gray-600 ml-3.5">
									Recusadas pela SEFAZ. Corrija e reenvie pelo Monitor Fiscal.
								</p>
							</div>
						)}

						{dados.pendentes > 0 && (
							<div>
								<p className="font-semibold text-gray-800 flex items-center gap-1.5 mb-1">
									<span className="w-2 h-2 rounded-full bg-amber-500"></span>
									{dados.pendentes} Nota(s) em Contingência/Aguardando
								</p>
								<p className="text-xs text-gray-600 ml-3.5">
									Transmitidas offline. Verifique o status no Monitor Fiscal.
								</p>
							</div>
						)}

						{qtdGaps > 0 && (
							<div>
								<p className="font-semibold text-gray-800 flex items-center gap-1.5 mb-1">
									<span className="w-2 h-2 rounded-full bg-red-500"></span>
									Quebra de Sequência
								</p>
								<p className="text-xs text-gray-600 ml-3.5 mb-2">
									Números detectados como não utilizados:
								</p>
								<div className="ml-3.5 flex flex-wrap gap-1 mb-2">
									{dados.lacunas.map(({ serie, de, ate }) => (
										<span key={`${serie}-${de}`} className="px-1.5 py-0.5 bg-white border border-gray-300 text-gray-700 rounded text-xs font-medium shadow-sm">
											Série {serie}: {de === ate ? de : `${de} a ${ate}`}
										</span>
									))}
								</div>
								<p className="text-[11px] text-gray-500 italic ml-3.5 mt-2 border-l-2 border-red-300 pl-2">
									* Acesse a tela do Monitor Fiscal para realizar a inutilização destas numerações caso não as tenha emitido em outro sistema.
								</p>
							</div>
						)}
						
					</div>
				)}
			</div>

			{/* Modal/Dialog de Confirmação */}
			{modalAberto && (
				<div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
					<div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
						<div className="bg-amber-50 px-6 py-4 flex items-start gap-4 border-b border-amber-100">
							<div className="bg-amber-100 text-amber-600 p-2 rounded-full shrink-0">
								<AlertTriangle size={24} />
							</div>
							<div>
								<h3 className="text-lg font-bold text-gray-800">Atenção às Pendências</h3>
								<p className="text-sm text-gray-600 mt-1">
									Existem notas rejeitadas, presas em contingência ou com quebra de sequência neste modelo.
								</p>
							</div>
						</div>
						<div className="p-6">
							<p className="text-sm text-gray-700 text-center mb-6">
								É altamente recomendado corrigir estes problemas antes do envio. 
								<strong> Tem certeza que deseja enviar o fechamento mesmo assim?</strong>
							</p>
							<div className="flex items-center gap-3 justify-center">
								<button 
									onClick={() => setModalAberto(false)}
									className="px-6 py-2 rounded border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50"
								>
									Cancelar
								</button>
								<button
									onClick={confirmarPendencias}
									className="px-6 py-2 rounded bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm"
								>
									Sim, Enviar Agora
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
			
			<MessageDialog
				open={!!erroEmail}
				title="Atenção aos E-mails"
				description={erroEmail || ""}
				variant="warning"
				buttons="ok"
				onClose={() => setErroEmail(null)}
				onOk={() => setErroEmail(null)}
			/>

			<MessageDialog
				open={confirmarReenvio}
				title="Fechamento já enviado"
				description={
					enviados.length > 0
						? `Este fechamento já foi enviado ${enviados.length} vez(es), a última em ${dataHora(enviados[0].data_fim)} para ${enviados[0].email_para}. Deseja enviar de novo?`
						: ""
				}
				variant="warning"
				buttons="confirmCancel"
				autoFocus="cancel"
				onConfirm={iniciarTransmissao}
				onClose={() => setConfirmarReenvio(false)}
			/>
		</>
	);
}
